<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex gap-x-2">
        <CustomerAutocomplete
          v-model="selectedCustomer"
          class="w-2/6 x-sm:w-full"
          label=""
          placeholder="Customer"
          customer
          hide-details
          :return-object="false"
          item-value="accountNumber"
        />
        <CfsDateField v-model="selectedDateRange" placeholder="Select date range" range />
      </div>
      <CfsDropdown
        icon="mdi-tray-arrow-down"
        :disabled="!selectedCustomer || !selectedDateRange"
        medium
        :loading="isLoading"
        color="primary"
        btn-width="32px"
        title="Download"
        offset-x
        :btn-type-text="false"
        :min-width="175"
        nudge-bottom="6px"
        rounded-menu="lg"
        :items="dropdownOptions"
      />
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, ref } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFileByType } from '@/helpers/download';
import { DocumentTypes, OutputTypes } from '@/constants/reports';
import { useToast } from '@/composables/useToast';
import date from '@/extensions/filters/date';
import DownloadIcon from '@/assets/img/icons/download/download-with-circle.svg';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';

export default defineComponent({
  name: 'TransloadStorageReport',
  components: {
    ReportWrapper,
    CustomerAutocomplete: defineAsyncComponent(
      () => import('@/components/autocomplete/CustomerAutocompleteComponent.vue')
    ),
  },
  setup() {
    const toast = useToast();

    const selectedCustomer = ref<string | null>(null);
    const selectedDateRange = ref<{ start: string; end: string } | null>(null);

    const { useDownloadInternalInventoryReport } = useReports();
    const { mutateAsync: downloadReport, isLoading } = useDownloadInternalInventoryReport();

    const download = async (outputType: string) => {
      if (!selectedCustomer.value || !selectedDateRange.value)
        return toast.info('Please select customer and date.');

      try {
        const { data } = await downloadReport({
          documentType: DocumentTypes.TRANSLOAD_STORAGE,
          outputType,
          customer: selectedCustomer.value,
          fromdate: selectedDateRange.value?.start,
          todate: selectedDateRange.value?.end,
        });

        if (!data) return;

        downloadFileByType(
          data,
          `Transload Storage Report ${date(selectedDateRange.value?.start)} - ${date(
            selectedDateRange.value?.end
          )}`,
          outputType
        );
      } catch (e) {
        console.log(e);
      }
    };

    const dropdownOptions = [
      {
        title: 'Download XLSX',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.XLSX),
      },
      {
        title: 'Download PDF',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.PDF),
      },
      {
        title: 'Download DOCX',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.DOCX),
      },
    ];

    return { selectedDateRange, selectedCustomer, isLoading, dropdownOptions };
  },
});
</script>
